import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Coins, Check, Loader2, Sparkles, Zap, Crown, CreditCard, Banknote, Copy, ArrowLeft, Upload, Clock } from "lucide-react";
import { fetchPaymentPackages, stripeCheckout, submitZelle } from "../paymentsApi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const PKG_ICON = { pkg_small: Zap, pkg_med: Sparkles, pkg_big: Crown };
const PKG_ACCENT = {
  pkg_small: "from-blue-500 to-blue-600",
  pkg_med: "from-purple-500 to-fuchsia-600",
  pkg_big: "from-amber-500 to-orange-600",
};

// Steps: select_pkg → choose_method → (stripe redirect) | zelle_form → zelle_pending
export default function CreditsModal({ open, onOpenChange, onPurchased }) {
  const [packages, setPackages] = useState([]);
  const [zelle, setZelle] = useState({ email: "crmpublimedios@gmail.com", name: "Números Fácil" });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("select_pkg");
  const [chosen, setChosen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Zelle form state
  const [zForm, setZForm] = useState({ sender_name: "", sender_email: "", reference: "", note: "", proof_data_url: "" });
  const [proofPreview, setProofPreview] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("select_pkg");
      setChosen(null);
      setZForm({ sender_name: "", sender_email: "", reference: "", note: "", proof_data_url: "" });
      setProofPreview("");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await fetchPaymentPackages();
        setPackages(data.packages || []);
        if (data.zelle) setZelle(data.zelle);
        if (user?.email) {
          setZForm((prev) => ({ ...prev, sender_email: prev.sender_email || user.email, sender_name: prev.sender_name || user.name }));
        }
      } catch {
        setPackages([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, user]);

  const pickPackage = (pkg) => { setChosen(pkg); setStep("choose_method"); };

  const payWithStripe = async () => {
    if (!chosen) return;
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const res = await stripeCheckout(chosen.id, origin);
      window.location.href = res.url;
    } catch (e) {
      toast({ title: "Error con Stripe", description: e?.response?.data?.detail || "No pudimos iniciar el pago." });
      setSubmitting(false);
    }
  };

  const handleProofUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Imagen muy pesada", description: "Máximo 5 MB" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setZForm((p) => ({ ...p, proof_data_url: ev.target.result }));
      setProofPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const submitZelleForm = async (e) => {
    e?.preventDefault?.();
    if (!chosen) return;
    if (!zForm.sender_name.trim() || !zForm.reference.trim()) {
      toast({ title: "Completa los campos", description: "Tu nombre y la referencia/confirmación son obligatorios." });
      return;
    }
    setSubmitting(true);
    try {
      await submitZelle({ package_id: chosen.id, ...zForm });
      setStep("zelle_pending");
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "No pudimos enviar tu comprobante." });
    } finally {
      setSubmitting(false);
    }
  };

  const copyZelleEmail = async () => {
    try {
      await navigator.clipboard.writeText(zelle.email);
      toast({ title: "Correo copiado", description: zelle.email });
    } catch {}
  };

  // ----------- Renders -----------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="credits-modal">
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-6 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-extrabold text-white flex items-center gap-2">
              <Coins className="w-6 h-6" /> Compra créditos para Tauro
            </DialogTitle>
            <DialogDescription className="text-orange-100">
              1 chat = 1 crédito · 1 análisis = 2 créditos
            </DialogDescription>
          </DialogHeader>
          {user && (
            <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 rounded-full px-4 py-1.5 text-sm font-semibold">
              <Coins className="w-4 h-4" /> Tus créditos: {user.credits ?? 0}
            </div>
          )}
        </div>

        <div className="p-6">
          {step === "select_pkg" && (
            <PackagesGrid packages={packages} loading={loading} onPick={pickPackage} />
          )}

          {step === "choose_method" && chosen && (
            <ChooseMethod
              pkg={chosen}
              onBack={() => setStep("select_pkg")}
              onCard={payWithStripe}
              onZelle={() => setStep("zelle_form")}
              loading={submitting}
            />
          )}

          {step === "zelle_form" && chosen && (
            <ZelleForm
              pkg={chosen}
              zelle={zelle}
              form={zForm}
              setForm={setZForm}
              proofPreview={proofPreview}
              onUpload={handleProofUpload}
              onSubmit={submitZelleForm}
              onCopy={copyZelleEmail}
              onBack={() => setStep("choose_method")}
              submitting={submitting}
            />
          )}

          {step === "zelle_pending" && (
            <ZellePending pkg={chosen} onClose={() => onOpenChange(false)} onAnother={() => setStep("select_pkg")} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
function PackagesGrid({ packages, loading, onPick }) {
  if (loading) return <p className="text-center text-gray-500 py-6">Cargando paquetes...</p>;
  return (
    <>
      <p className="text-sm text-gray-600 mb-4">Selecciona un paquete:</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {packages.map((p) => {
          const Icon = PKG_ICON[p.id] || Zap;
          const accent = PKG_ACCENT[p.id] || "from-blue-500 to-blue-600";
          const total = p.credits + (p.bonus || 0);
          return (
            <button
              key={p.id}
              data-testid={`pkg-${p.id}`}
              onClick={() => onPick(p)}
              className="text-left border-2 border-blue-100 hover:border-[#1d6cb1] rounded-2xl p-5 transition relative overflow-hidden bg-white hover:shadow-lg"
            >
              {p.bonus > 0 && (
                <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                  +{p.bonus} BONUS
                </span>
              )}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center mb-3 shadow`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-extrabold text-lg text-[#0e4a7a]">{p.name}</h3>
              <p className="mt-2 text-3xl font-extrabold text-gray-900">
                {total}
                <span className="text-sm font-normal text-gray-500 ml-1">créditos</span>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {p.bonus > 0 ? <>{p.credits} + <strong className="text-emerald-600">{p.bonus} bonus</strong></> : `${p.credits} consultas`}
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-[#0e4a7a]">${p.price_usd ?? p.price ?? p.price_dop}</span>
                <span className="text-xs text-gray-500">USD</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ------------------------------------------------------------------
function ChooseMethod({ pkg, onBack, onCard, onZelle, loading }) {
  const total = pkg.credits + (pkg.bonus || 0);
  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#0e4a7a] mb-4">
        <ArrowLeft className="w-4 h-4" /> Cambiar paquete
      </button>
      <div className="rounded-2xl bg-blue-50/40 border border-blue-100 p-4 mb-5">
        <p className="text-xs uppercase tracking-wider text-gray-500">Vas a comprar</p>
        <p className="font-extrabold text-lg text-[#0e4a7a]">{pkg.name} · {total} créditos</p>
        <p className="text-2xl font-extrabold text-amber-600">${pkg.price_usd ?? pkg.price ?? pkg.price_dop} USD</p>
      </div>

      <p className="text-sm text-gray-600 mb-3">Elige un método de pago:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          data-testid="method-card-btn"
          onClick={onCard}
          disabled={loading}
          className="group flex flex-col items-start gap-2 p-5 rounded-2xl border-2 border-blue-200 hover:border-[#1d6cb1] hover:shadow-lg transition text-left disabled:opacity-60"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1d6cb1] to-[#2a83ce] text-white flex items-center justify-center">
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <CreditCard className="w-6 h-6" />}
          </div>
          <h3 className="font-extrabold text-[#0e4a7a]">Tarjeta de Crédito/Débito</h3>
          <p className="text-xs text-gray-600">Visa, MasterCard, AmEx — pago instantáneo, créditos al momento.</p>
          <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600">⚡ Acreditación inmediata</span>
        </button>

        <button
          data-testid="method-zelle-btn"
          onClick={onZelle}
          className="group flex flex-col items-start gap-2 p-5 rounded-2xl border-2 border-purple-200 hover:border-purple-500 hover:shadow-lg transition text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white flex items-center justify-center">
            <Banknote className="w-6 h-6" />
          </div>
          <h3 className="font-extrabold text-[#0e4a7a]">Zelle</h3>
          <p className="text-xs text-gray-600">Transferencia desde tu banco a <strong>crmpublimedios@gmail.com</strong></p>
          <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-600">⏱ Acreditación tras revisión (24h)</span>
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
function ZelleForm({ pkg, zelle, form, setForm, proofPreview, onUpload, onSubmit, onCopy, onBack, submitting }) {
  const total = pkg.credits + (pkg.bonus || 0);
  return (
    <form onSubmit={onSubmit}>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#0e4a7a] mb-4">
        <ArrowLeft className="w-4 h-4" /> Cambiar método
      </button>

      <div className="rounded-2xl bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-200 p-5 mb-5">
        <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Paso 1: Transfiere por Zelle</p>
        <div className="space-y-2 text-sm">
          <Row label="Correo Zelle">
            <span className="font-mono font-bold text-[#0e4a7a]">{zelle.email}</span>
            <button type="button" onClick={onCopy} className="ml-2 inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800">
              <Copy className="w-3 h-3" /> Copiar
            </button>
          </Row>
          <Row label="A nombre de"><span>{zelle.name}</span></Row>
          <Row label="Monto exacto"><span className="font-extrabold text-amber-600">${pkg.price_usd ?? pkg.price ?? pkg.price_dop} USD</span></Row>
          <Row label="Concepto sugerido"><span className="font-mono text-[#0e4a7a]">TAURO {pkg.id.toUpperCase()}</span></Row>
        </div>
      </div>

      <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Paso 2: Enviar comprobante</p>

      <div className="space-y-3">
        <Field label="Tu nombre">
          <input
            data-testid="zelle-sender-name"
            value={form.sender_name}
            onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-purple-100 focus:border-purple-500 outline-none text-sm"
            placeholder="Como aparece en tu cuenta"
            required
          />
        </Field>
        <Field label="Tu correo (opcional)">
          <input
            type="email"
            data-testid="zelle-sender-email"
            value={form.sender_email}
            onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-purple-100 focus:border-purple-500 outline-none text-sm"
            placeholder="email@ejemplo.com"
          />
        </Field>
        <Field label="Número de confirmación / referencia">
          <input
            data-testid="zelle-reference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-purple-100 focus:border-purple-500 outline-none text-sm font-mono"
            placeholder="Ej: PXM12345AB"
            required
          />
        </Field>
        <Field label="Comprobante (opcional pero recomendado)">
          <label className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-purple-200 cursor-pointer hover:bg-purple-50 transition text-sm text-purple-700">
            <Upload className="w-4 h-4" />
            <span>Subir captura del comprobante</span>
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="zelle-proof-input" />
          </label>
          {proofPreview && (
            <img src={proofPreview} alt="Comprobante" className="mt-3 max-h-40 rounded-lg border border-purple-200" />
          )}
        </Field>
        <Field label="Nota (opcional)">
          <textarea
            data-testid="zelle-note"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-purple-100 focus:border-purple-500 outline-none text-sm"
            placeholder="Cualquier detalle adicional"
          />
        </Field>
      </div>

      <button
        data-testid="zelle-submit-btn"
        type="submit"
        disabled={submitting}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold shadow-md hover:shadow-lg disabled:opacity-60 transition"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Check className="w-4 h-4" /> Enviar comprobante</>}
      </button>

      <p className="text-[11px] text-gray-500 text-center mt-3">
        Tras revisión (máx 24h) acreditaremos los créditos en tu cuenta automáticamente.
      </p>
    </form>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-purple-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ------------------------------------------------------------------
function ZellePending({ pkg, onClose, onAnother }) {
  const total = pkg ? pkg.credits + (pkg.bonus || 0) : 0;
  return (
    <div className="text-center py-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white flex items-center justify-center mx-auto mb-4 shadow-xl">
        <Clock className="w-10 h-10" />
      </div>
      <h3 className="text-xl font-extrabold text-[#0e4a7a]">Comprobante recibido</h3>
      <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
        Vamos a verificar tu transferencia y acreditar <strong>{total} créditos</strong> a tu cuenta. Recibirás los créditos en máximo <strong>24 horas</strong>.
      </p>
      <p className="text-xs text-gray-500 mt-3">
        Puedes ver el estado en <strong>Mi Cuenta → Pagos</strong>.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <button onClick={onAnother} className="px-4 py-2 rounded-full bg-blue-50 text-[#1d6cb1] font-semibold text-sm">Comprar otro paquete</button>
        <button onClick={onClose} className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold text-sm">Cerrar</button>
      </div>
    </div>
  );
}
